import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Shield, 
  Users, 
  BarChart3, 
  Zap, 
  CheckCircle, 
  Star,
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Play,
  Award,
  TrendingUp,
  Clock,
  DollarSign,
  Lock,
  Headphones
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const LandingPage = ({ onLoginClick, onSignUpClick }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const features = [
    {
      icon: Users,
      title: "Lead Management",
      description: "Capture, track, and convert leads with our advanced Kanban pipeline system.",
      benefits: ["Automated lead scoring", "Real-time notifications", "Source tracking"]
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Get deep insights into your business performance with comprehensive reporting.",
      benefits: ["Revenue analytics", "Conversion tracking", "Performance metrics"]
    },
    {
      icon: Shield,
      title: "Multi-Tenant Architecture",
      description: "Secure, scalable platform designed for agencies and enterprise clients.",
      benefits: ["Data isolation", "Custom branding", "Role-based access"]
    },
    {
      icon: Zap,
      title: "Automation Engine",
      description: "Streamline renewals, commissions, and communications automatically.",
      benefits: ["Auto renewals", "Commission calculation", "Email automation"]
    },
    {
      icon: Lock,
      title: "Enterprise Security",
      description: "Bank-level security with encryption, compliance, and audit trails.",
      benefits: ["256-bit encryption", "SOC 2 compliant", "Regular audits"]
    },
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Dedicated support team with insurance industry expertise.",
      benefits: ["Live chat support", "Phone assistance", "Training included"]
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "Premium Insurance Group",
      role: "Agency Owner",
      content: "CRM INS PRO transformed our agency. We increased our conversion rate by 45% and reduced processing time by 60%.",
      rating: 5,
      avatar: "SJ"
    },
    {
      name: "Mike Rodriguez",
      company: "Coastal Insurance Services",
      role: "Operations Manager", 
      content: "The multi-tenant features are incredible. We can manage multiple brands under one platform while maintaining complete separation.",
      rating: 5,
      avatar: "MR"
    },
    {
      name: "Jennifer Lee",
      company: "Metropolitan Insurance",
      role: "IT Director",
      content: "Finally, a CRM built specifically for insurance. The automation features save us 20+ hours per week.",
      rating: 5,
      avatar: "JL"
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "$49",
      period: "per user/month",
      description: "Perfect for small agencies",
      features: [
        "Up to 5 users",
        "Lead management",
        "Basic analytics", 
        "Email support",
        "Standard integrations"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Professional", 
      price: "$99",
      period: "per user/month",
      description: "Ideal for growing agencies",
      features: [
        "Up to 25 users",
        "Advanced analytics",
        "Automation engine",
        "Priority support",
        "Custom integrations",
        "White label branding"
      ],
      cta: "Request Demo",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large organizations",
      features: [
        "Unlimited users",
        "Multi-tenant architecture",
        "Advanced security",
        "Dedicated support",
        "Custom development",
        "SLA guarantee"
      ],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "Is CRM INS PRO specifically designed for insurance?",
      answer: "Yes, CRM INS PRO is built exclusively for the insurance industry with features like policy management, renewal automation, commission tracking, and carrier integrations."
    },
    {
      question: "Can I customize the platform for my brand?",
      answer: "Absolutely! Our white-label solution allows you to customize colors, logos, and branding to match your agency's identity."
    },
    {
      question: "How secure is my data?",
      answer: "We use enterprise-grade security with 256-bit encryption, SOC 2 compliance, and regular security audits. Your data is protected with the highest industry standards."
    },
    {
      question: "Do you offer data migration services?",
      answer: "Yes, our team provides free data migration from your existing CRM system as part of our onboarding process."
    },
    {
      question: "What integrations are available?",
      answer: "We integrate with major carriers, email platforms, phone systems, and accounting software. Custom integrations are available for Enterprise clients."
    },
    {
      question: "Is training included?",
      answer: "Yes, we provide comprehensive training for your team, including live sessions, video tutorials, and ongoing support."
    }
  ];

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    // Handle demo request submission
    alert('Thank you! Our team will contact you within 24 hours to schedule your personalized demo.');
    setDemoForm({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="bg-mg-green text-white p-2 rounded-lg mr-3">
                <div className="text-sm font-bold">CIP</div>
              </div>
              <div className="text-xl font-bold text-mg-green">
                CRM INS PRO
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-mg-green transition-colors">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-mg-green transition-colors">Pricing</a>
              <a href="#testimonials" className="text-gray-600 hover:text-mg-green transition-colors">Reviews</a>
              <a href="#faq" className="text-gray-600 hover:text-mg-green transition-colors">FAQ</a>
              <Button 
                variant="outline" 
                onClick={onLoginClick}
                className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
              >
                Sign In
              </Button>
              <Button 
                onClick={onSignUpClick}
                className="bg-mg-green hover:bg-mg-green/90 text-white"
              >
                Get Started
              </Button>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                <a href="#features" className="block px-3 py-2 text-gray-600 hover:text-mg-green">Features</a>
                <a href="#pricing" className="block px-3 py-2 text-gray-600 hover:text-mg-green">Pricing</a>
                <a href="#testimonials" className="block px-3 py-2 text-gray-600 hover:text-mg-green">Reviews</a>
                <a href="#faq" className="block px-3 py-2 text-gray-600 hover:text-mg-green">FAQ</a>
                <div className="pt-4 pb-2 space-y-2">
                  <Button 
                    variant="outline" 
                    onClick={onLoginClick}
                    className="w-full border-mg-green text-mg-green hover:bg-mg-green hover:text-white"
                  >
                    Sign In
                  </Button>
                  <Button 
                    onClick={onSignUpClick}
                    className="w-full bg-mg-green hover:bg-mg-green/90 text-white"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-slate-800 to-mg-green text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='7' cy='7' r='7'/%3E%3Ccircle cx='53' cy='53' r='7'/%3E%3Ccircle cx='30' cy='30' r='7'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Hero Content */}
            <div className="text-center lg:text-left">
              {/* Trust Badge */}
              <div className="inline-flex items-center bg-mg-green/20 backdrop-blur-sm rounded-full px-4 py-2 mb-8 border border-mg-green/30">
                <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.602-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-white font-medium text-sm">Rated #1 Insurance CRM Platform</span>
                <div className="flex ml-2">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.602-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>

              {/* Main Headline */}
              <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
                <span className="block text-white">Transform Your</span>
                <span className="block text-[#facc15]">
                  Insurance Business
                </span>
                <span className="block text-3xl lg:text-4xl font-medium text-gray-200 mt-2">
                  in 30 Days or Less
                </span>
              </h1>

              {/* Compelling Sub-headline */}
              <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed">
                Join 2,500+ insurance professionals who've increased their conversion rates by 
                <span className="font-bold text-mg-green"> 45%</span> and automated 
                <span className="font-bold text-mg-green"> 80%</span> of their renewals with our AI-powered CRM.
              </p>

              {/* Key Benefits - Quick Scan */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 mb-10 text-gray-200">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-mg-green mr-2" />
                  <span className="font-medium">Setup in 5 minutes</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-mg-green mr-2" />
                  <span className="font-medium">No credit card required</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-mg-green mr-2" />
                  <span className="font-medium">30-day guarantee</span>
                </div>
              </div>

              {/* Enhanced CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <Button 
                  size="lg" 
                  className="bg-mg-green text-white hover:bg-emerald-600 text-lg px-8 py-4 font-bold shadow-2xl transform hover:scale-105 transition-all duration-200 border-2 border-transparent hover:border-mg-green"
                  onClick={() => document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  <span className="flex items-center">
                    <Play className="mr-2 h-6 w-6" />
                    Get Free Demo Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </span>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-gray-400 text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-4 font-bold backdrop-blur-sm bg-white/10 transform hover:scale-105 transition-all duration-200"
                >
                  <div className="flex items-center">
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1M9 16v-6a4 4 0 118 0v6M5 12H4a2 2 0 00-2 2v4a2 2 0 002 2h16a2 2 0 002-2v-4a2 2 0 00-2-2h-1" />
                    </svg>
                    Watch 2-Min Demo
                  </div>
                </Button>
              </div>

              {/* Social Proof Pills */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 text-sm text-gray-300">
                <div className="flex items-center bg-gray-800/50 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-700">
                  <TrendingUp className="w-4 h-4 mr-2 text-mg-green" />
                  <span><strong className="text-white">2,500+</strong> Active Users</span>
                </div>
                <div className="flex items-center bg-gray-800/50 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-700">
                  <DollarSign className="w-4 h-4 mr-2 text-mg-green" />
                  <span><strong className="text-white">$2.1B+</strong> Premiums Managed</span>
                </div>
                <div className="flex items-center bg-gray-800/50 backdrop-blur-sm rounded-full px-4 py-2 border border-gray-700">
                  <Award className="w-4 h-4 mr-2 text-mg-green" />
                  <span><strong className="text-white">98%</strong> Satisfaction Rate</span>
                </div>
              </div>
            </div>
            
            {/* Right Column - Interactive Dashboard Preview */}
            <div className="relative">
              {/* Floating Cards Animation */}
              <div className="relative">
                {/* Main Dashboard Card */}
                <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-2 hover:rotate-0 transition-transform duration-500 border border-gray-200">
                  <div className="bg-gradient-to-r from-mg-green to-emerald-600 text-white p-6 rounded-xl mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-white">Live Dashboard</h3>
                        <div className="flex items-center text-green-100">
                          <div className="w-2 h-2 bg-green-300 rounded-full mr-2 animate-pulse"></div>
                          <span className="text-sm">Real-time</span>
                        </div>
                      </div>
                      <p className="text-green-100 text-sm">Your insurance metrics at a glance</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-mg-green rounded-full mr-3"></div>
                        <span className="text-gray-700 font-medium">Total Leads</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-mg-green">1,247</div>
                        <div className="text-sm text-green-600">+12% this month</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                        <span className="text-gray-700 font-medium">Conversion Rate</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-blue-600">68.5%</div>
                        <div className="text-sm text-green-600">+45% increase</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                        <span className="text-gray-700 font-medium">Monthly Revenue</span>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-orange-600">$89,420</div>
                        <div className="text-sm text-green-600">+28% growth</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 text-center">
                    <Button size="sm" variant="outline" className="border-mg-green text-mg-green hover:bg-mg-green hover:text-white">
                      View Full Dashboard
                    </Button>
                  </div>
                </div>

                {/* Floating Testimonial Card */}
                <div className="absolute -top-4 -left-8 bg-white rounded-xl shadow-xl p-4 max-w-xs transform -rotate-6 hover:rotate-0 transition-transform duration-300 border-l-4 border-mg-green hidden lg:block">
                  <div className="flex items-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 italic mb-2">
                    "Increased our conversion rate by 45% in just 30 days!"
                  </p>
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-mg-green text-white rounded-full flex items-center justify-center mr-2">
                      <span className="text-xs font-bold">SJ</span>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-900">Sarah Johnson</div>
                      <div className="text-xs text-gray-500">Agency Owner</div>
                    </div>
                  </div>
                </div>

                {/* Floating Stats Card */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-r from-mg-green to-emerald-500 text-white rounded-xl shadow-xl p-4 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:block border border-emerald-400">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">45%</div>
                    <div className="text-xs text-emerald-100">Avg. Conversion Increase</div>
                  </div>
                </div>

                {/* Floating Feature Card */}
                <div className="absolute top-16 -right-12 bg-gradient-to-r from-slate-700 to-gray-800 text-white rounded-lg shadow-xl p-3 transform rotate-12 hover:rotate-6 transition-transform duration-300 hidden lg:block border border-slate-600">
                  <div className="flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-mg-green" />
                    <div className="text-sm font-medium text-white">AI-Powered</div>
                  </div>
                </div>
              </div>

              {/* Background Glow Effect */}
              <div className="absolute -inset-10 bg-gradient-to-r from-mg-green/20 to-emerald-400/20 rounded-full blur-3xl -z-10"></div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-gray-400">
          <div className="flex flex-col items-center cursor-pointer hover:text-white transition-colors" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
            <span className="text-sm mb-2">Scroll to explore</span>
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
              <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-gray-600">Trusted by 2,500+ insurance professionals</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center opacity-60">
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">2,500+</div>
              <div className="text-sm text-gray-600">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">$2.1B+</div>
              <div className="text-sm text-gray-600">Premiums Managed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">98%</div>
              <div className="text-sm text-gray-600">Customer Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-mg-green">45%</div>
              <div className="text-sm text-gray-600">Avg. Conversion Increase</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need to Grow Your Insurance Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Purpose-built for insurance professionals with features that address your specific challenges and workflows.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-mg-green/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-mg-green" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-mg-green mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of insurance professionals who've transformed their business
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-mg-green text-white rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm font-bold">{testimonial.avatar}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that fits your agency size and needs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card key={index} className={`relative border-2 ${plan.popular ? 'border-mg-green shadow-xl' : 'border-gray-200'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-mg-green text-white px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-5xl font-bold text-mg-green">{plan.price}</span>
                      <span className="text-gray-500 ml-2">/{plan.period}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-mg-green mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-mg-green hover:bg-mg-green/90' : 'bg-gray-900 hover:bg-gray-800'} text-white`}
                    onClick={() => document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Request Section */}
      <section id="demo-form" className="py-20 bg-mg-green text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold mb-4">
              Ready to Transform Your Insurance Business?
            </h2>
            <p className="text-xl text-green-100">
              Schedule a personalized demo and see how CRM INS PRO can increase your conversion rates by 45%
            </p>
          </div>
          
          <Card className="bg-white text-gray-900">
            <CardContent className="p-8">
              <form onSubmit={handleDemoSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Business Email *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      placeholder="john@insuranceagency.com"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <Input
                      id="company"
                      type="text"
                      required
                      value={demoForm.company}
                      onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      placeholder="Insurance Agency LLC"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      type="tel"
                      value={demoForm.phone}
                      onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Tell us about your current challenges
                  </label>
                  <Textarea
                    id="message"
                    value={demoForm.message}
                    onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                    placeholder="What are your biggest challenges with lead management, policy renewals, or commission tracking?"
                    rows={4}
                    className="w-full"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-mg-green hover:bg-mg-green/90 text-white text-lg py-3"
                >
                  Schedule My Free Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <p className="text-center text-sm text-gray-600">
                  Our team will contact you within 24 hours to schedule your personalized demo. 
                  No sales pressure, just a genuine look at how CRM INS PRO can help your business.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about CRM INS PRO
            </p>
          </div>
          
          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="bg-mg-green text-white p-2 rounded-lg mr-3">
                  <div className="text-sm font-bold">CIP</div>
                </div>
                <div className="text-xl font-bold">CRM INS PRO</div>
              </div>
              <p className="text-gray-400 mb-6">
                The most powerful insurance CRM platform designed to help agencies and brokers 
                increase conversions, automate processes, and grow their business.
              </p>
              <div className="flex space-x-4">
                <div className="flex items-center text-gray-400">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>(555) 123-4567</span>
                </div>
                <div className="flex items-center text-gray-400">
                  <Mail className="w-4 h-4 mr-2" />
                  <span>sales@crminspro.com</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Docs</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Budget Digital Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;